'use client';
import React, { useEffect, useState } from 'react';
import { Zap, Users, AlertTriangle, Activity, Cpu, Globe } from 'lucide-react';

interface StatCard {
  id: string;
  icon: React.ElementType;
  label: string;
  value: string;
  sub: string;
  color: string;
  alert?: boolean;
  trend?: string;
  trendUp?: boolean;
}

const BASE_STATS: StatCard[] = [
  {
    id: 'stat-organisms',
    icon: Users,
    label: 'Active Organisms',
    value: '3,482',
    sub: '3,482 AI + 0 real',
    color: 'text-primary',
    trend: '+12 last min',
    trendUp: true,
  },
  {
    id: 'stat-particles',
    icon: Zap,
    label: 'Energy Particles',
    value: '11,847',
    sub: 'Across 40K × 40K arena',
    color: 'text-neon-yellow',
    trend: '~200/min respawn',
    trendUp: true,
  },
  {
    id: 'stat-corruption',
    icon: AlertTriangle,
    label: 'Corruption Rate',
    value: '4.1%',
    sub: 'Glitch Spore density',
    color: 'text-neon-green',
    alert: true,
    trend: '+0.3% spreading',
    trendUp: false,
  },
  {
    id: 'stat-absorptions',
    icon: Activity,
    label: 'Absorptions / min',
    value: '247',
    sub: 'Arena-wide event rate',
    color: 'text-neon-magenta',
    trend: 'Peak: 312/min',
    trendUp: true,
  },
  {
    id: 'stat-leakers',
    icon: Cpu,
    label: 'Neon Leakers',
    value: '200',
    sub: 'Active particle spawners',
    color: 'text-neon-purple',
    trend: 'Leaking continuously',
    trendUp: true,
  },
  {
    id: 'stat-zones',
    icon: Globe,
    label: 'Active Zones',
    value: '8 / 16',
    sub: 'Quadrant activity',
    color: 'text-neon-orange',
    trend: '4 high-density zones',
    trendUp: true,
  },
];

export default function LiveArenaStats() {
  const [stats, setStats] = useState<StatCard[]>(BASE_STATS);

  useEffect(() => {
    // BACKEND INTEGRATION: WebSocket subscription to arena stats stream
    const interval = setInterval(() => {
      setStats(prev => prev.map(s => {
        if (s.id === 'stat-organisms') {
          const v = 3400 + Math.floor(Math.random() * 200);
          return { ...s, value: v.toLocaleString(), sub: `${v} AI + 0 real` };
        }
        if (s.id === 'stat-particles') {
          const v = 11000 + Math.floor(Math.random() * 1500);
          return { ...s, value: v.toLocaleString() };
        }
        if (s.id === 'stat-absorptions') {
          const v = 200 + Math.floor(Math.random() * 120);
          return { ...s, value: v.toString() };
        }
        return s;
      }));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
      {stats.map(stat => (
        <div
          key={stat.id}
          className={`stat-card ${stat.alert ? 'border-neon-green/30 corruption-warning' : ''}`}
        >
          <div className="flex items-center justify-between mb-3">
            <stat.icon size={16} className={stat.color} />
            {stat.alert && (
              <span className="text-xs font-mono text-neon-green px-1.5 py-0.5 rounded border border-neon-green/30 bg-neon-green/5">
                ACTIVE
              </span>
            )}
          </div>
          <div className={`text-xl font-bold font-mono ${stat.color} mb-0.5`}>{stat.value}</div>
          <div className="text-xs font-medium text-foreground mb-1">{stat.label}</div>
          <div className="text-xs text-muted-foreground">{stat.sub}</div>
          {stat.trend && (
            <div className={`text-xs font-mono mt-2 ${stat.trendUp ? 'text-neon-green' : 'text-red-400'}`}>
              {stat.trendUp ? '↑' : '↓'} {stat.trend}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}